/**
 * UI Components - Moon Moon Agency
 * Handles read more functionality, loader states, and UI components
 */

/*********************************************************************************************
 * 🚀 LOADER MODULE
 */
const LoaderManager = {
	// Local configuration
	config: {
		classes: {
			loading: 'mm-loading',
			active: 'mm-active',
			expanded: 'mm-expanded'
		},
		selectors: {
			readMoreToggle: '[data-read-more-toggle]',
			readMoreContainer: '[data-read-more]',
			readMoreText: '[data-read-more-text]',
			readMoreLabelShow: '[data-read-more-show]',
			readMoreLabelHide: '[data-read-more-hide]'
		}
	},

	/**
	 * Add loading state to specified element
	 */
	loadThis(elem) {
		if (!elem) {
			window.log('warn', 'LoaderManager', 'No element provided');
			return;
		}
		elem.classList.add(this.config.classes.loading);
		window.emit('global:loader-activated', { element: elem.tagName });
	}
};

/*********************************************************************************************
 * 📖 READ MORE MODULE
 */
const ReadMoreManager = {
	/**
	 * Toggle read more state
	 */
	toggle(btn) {
		const container = btn.closest(LoaderManager.config.selectors.readMoreContainer);
		if (!container) return;

		const textElement = container.querySelector(LoaderManager.config.selectors.readMoreText);
		const labelShow = btn.querySelector(LoaderManager.config.selectors.readMoreLabelShow);
		const labelHide = btn.querySelector(LoaderManager.config.selectors.readMoreLabelHide);

		if (!textElement || !labelShow || !labelHide) return;

		const expanded = btn.getAttribute('aria-expanded') === 'true';
		const newState = !expanded;

		// Update state
		textElement.classList.toggle(LoaderManager.config.classes.expanded, newState);
		btn.setAttribute('aria-expanded', newState.toString());
		labelShow.hidden = newState;
		labelHide.hidden = !newState;

		window.emit('global:read-more-toggled', { expanded: newState });
	},

	/**
	 * Hide the toggle button when the collapsed content is not truncated.
	 * Re-checks on resize because available width drives the line count.
	 */
	_updateVisibility(btn) {
		const container = btn.closest(LoaderManager.config.selectors.readMoreContainer);
		if (!container) return;
		const textElement = container.querySelector(LoaderManager.config.selectors.readMoreText);
		if (!textElement) return;

		const check = () => {
			const expanded = btn.getAttribute('aria-expanded') === 'true';
			if (expanded) {
				btn.hidden = false;
				return;
			}
			btn.hidden = textElement.scrollHeight <= textElement.clientHeight + 1;
		};

		check();
		window.addEventListener('resize', window.debounce(check, 150));
	},

	/**
	 * Initialize read more functionality
	 */
	init() {
		const toggleButtons = window.getElements(LoaderManager.config.selectors.readMoreToggle);
		if (!toggleButtons.length) return;

		toggleButtons.forEach((btn) => {
			// Initialize aria-expanded if not present
			if (!btn.hasAttribute('aria-expanded')) {
				btn.setAttribute('aria-expanded', 'false');
			}

			this._updateVisibility(btn);

			// Click event
			btn.addEventListener('click', () => {
				this.toggle(btn);
			});

			// Keyboard support (Enter/Space)
			btn.addEventListener('keydown', (e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					this.toggle(btn);
				}
			});
		});
	}
};

/*********************************************************************************************
 * 🔗 EXTERNAL LINKS MODULE
 */
const ExternalLinksManager = {
	/**
	 * Process external links to open in new tab
	 */
	init() {
		const links = window.getElements('a:not(.consent-wrapper)');
		let processedCount = 0;

		links.forEach((link) => {
			if (link.hostname !== window.location.hostname) {
				link.target = '_blank';
				link.rel = 'noreferrer noopener';
				processedCount++;
			}
		});

	}
};

/*********************************************************************************************
 * 📏 DOCUMENT HEIGHT MODULE
 */
const DocumentHeightManager = {
	/**
	 * Update document height CSS variable
	 */
	update() {
		const newHeight = `${window.innerHeight}px`;
		document.documentElement.style.setProperty('--doc-height', newHeight);
		window.emit('global:document-height-updated', { height: newHeight });
	}
};

/*********************************************************************************************
 * 🎠 SPLIDE MODULE
 */
const SplideManager = {
	/**
	 * Initialize all Splide carousels with their data-splide configurations
	 */
	init() {
		const splideElements = window.getElements('.splide');
		if (!splideElements.length || typeof Splide === 'undefined') return;

		let initializedCount = 0;
		const sectionsWithSplide = [];

		splideElements.forEach(element => {
			// Skip already initialized sliders
			if (element._splide) return;

			// Skip product media sliders - they're synced and initialized in mm-product-medias.liquid
			const isProductMediaSlider = element.hasAttribute('data-main-slider') ||
										element.hasAttribute('data-thumbnail-slider');
			if (isProductMediaSlider) return;

			try {
				const configData = element.dataset.splide;
				let splideInstance;

				if (configData) {
					try {
						const config = JSON.parse(configData);
						splideInstance = new Splide(element, config).mount();
					} catch (jsonError) {
						window.log('warn', 'SplideManager', `Invalid JSON in data-splide, using defaults: ${element.className}`);
						splideInstance = new Splide(element).mount();
					}
				} else {
					// Configuration par défaut si pas de data-splide
					splideInstance = new Splide(element).mount();
				}

				// Gérer la progress bar si elle existe
				const progressBar = element.querySelector('.splide__progress__bar');
				if (progressBar && splideInstance) {
					const updateProgress = function() {
						try {
							const end = splideInstance.Components.Controller.getEnd() + 1;
							const rate = Math.min((splideInstance.index + 1) / end, 1);
							progressBar.style.width = String(100 * rate) + '%';
						} catch (error) {
							window.log('error', 'SplideManager', 'Progress bar update failed');
						}
					};

					// Initialiser immédiatement et au montage
					splideInstance.on('mounted ready', updateProgress);
					splideInstance.on('move moved', updateProgress);

					// Force l'initialisation après un court délai
					requestAnimationFrame(() => {
						setTimeout(updateProgress, 50);
					});
				}

				// Masquer les arrows si tous les slides sont visibles
				const arrowsEl = element.querySelector('.splide__arrows');
				if (arrowsEl) {
					const toggleArrows = () => {
						const overflow = splideInstance.Components.Layout.isOverflow();
						arrowsEl.style.visibility = overflow ? '' : 'hidden';
					};
					splideInstance.on('mounted resize', toggleArrows);
				}

				// Collecter les sections
				const sectionElement = element.closest('section');
				if (sectionElement) {
					const sectionName = sectionElement.className || 'unnamed-section';
					if (!sectionsWithSplide.includes(sectionName)) {
						sectionsWithSplide.push(sectionName);
					}
				}

				initializedCount++;

			} catch (error) {
				window.log('error', 'SplideManager', `Initialization failed for element: ${element.className}`);

				// Initialiser avec config par défaut en cas d'erreur
				try {
					new Splide(element).mount();
					initializedCount++;
				} catch (fallbackError) {
					window.log('error', 'SplideManager', `Fallback initialization failed: ${fallbackError.message}`);
				}
			}
		});

		// Émettre un seul événement global avec la liste des sections
		if (initializedCount > 0) {
			window.emit('global:splide-initialized', {
				count: initializedCount,
				sections: sectionsWithSplide
			});
		}
	}
};

/*********************************************************************************************
 * 🔻 DETAILS ACCORDION MODULE
 *
 * Anime l'ouverture/fermeture des <details> de maniere cross-browser.
 * Le CSS ::details-content (block-size 0 -> auto) n'anime que sur Chrome
 * (interpolate-size non supporte par Safari/Firefox) : on anime ici la hauteur
 * du wrapper de contenu en pixels via Web Animations API, interpolee partout.
 *
 * Pre-requis markup : chaque <details> a un seul element wrapper apres son <summary>.
 */
const DetailsAccordionManager = {
	config: {
		selector: 'details',
		duration: 550,
		easing: 'cubic-bezier(0.625, 0.05, 0, 1)',
	},

	init() {
		this._readMotionTokens();

		// Delegation : un seul listener pour tous les <details> (y compris ceux
		// injectes apres coup, ex: panneau filtres ouvert dynamiquement).
		document.addEventListener('click', (e) => {
			const summary = e.target.closest('summary');
			if (!summary) return;
			const details = summary.parentElement;
			if (!details || details.tagName !== 'DETAILS') return;
			if (!details.matches(this.config.selector)) return;
			this._toggle(e, details);
		});
		window.log('info', '🔻 [DETAILS]', 'Initialized');
	},

	/**
	 * Aligne le timing sur les accordeons du panneau filtres, qui animent
	 * grid-template-rows en --duration-short / --cubic-bezier-fast (mm-variables.css).
	 * Les valeurs de config servent de fallback si les variables sont absentes.
	 */
	_readMotionTokens() {
		const styles = getComputedStyle(document.documentElement);

		const duration = this._parseDuration(styles.getPropertyValue('--duration-short'));
		if (duration) this.config.duration = duration;

		const easing = styles.getPropertyValue('--cubic-bezier-fast').trim();
		if (easing) this.config.easing = easing;
	},

	/**
	 * "550ms" | "0.55s" -> 550. Retourne null si non parsable.
	 */
	_parseDuration(raw) {
		const value = raw.trim();
		if (!value) return null;
		const number = parseFloat(value);
		if (!Number.isFinite(number)) return null;
		return value.endsWith('ms') ? number : number * 1000;
	},

	/**
	 * Le wrapper anime = unique enfant direct du <details> hors <summary>.
	 */
	_getContent(details) {
		return [...details.children].find((c) => c !== details.querySelector(':scope > summary'));
	},

	_toggle(e, details) {
		// Respect prefers-reduced-motion : toggle natif instantane.
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

		const content = this._getContent(details);
		if (!content) return;

		// Une animation en cours sur ce details : la laisser finir (toggle natif).
		if (details._mmAnimating) return;

		e.preventDefault(); // on gere le toggle nous-memes

		const isOpen = details.open;
		details._mmAnimating = true;

		if (!isOpen) {
			// OUVERTURE : open d'abord pour que le contenu soit mesurable.
			details.open = true;
			const target = content.scrollHeight;
			this._animate(details, content, 0, target);
		} else {
			// FERMETURE : du courant vers 0, puis open=false a la fin.
			const start = content.scrollHeight;
			this._animate(details, content, start, 0, () => {
				details.open = false;
			});
		}
	},

	_animate(details, content, from, to, onFinish) {
		// overflow:hidden pendant l'anim pour clipper le contenu.
		const prevOverflow = content.style.overflow;
		content.style.overflow = 'hidden';

		const anim = content.animate(
			[{ blockSize: `${from}px` }, { blockSize: `${to}px` }],
			{ duration: this.config.duration, easing: this.config.easing }
		);

		anim.onfinish = () => {
			content.style.overflow = prevOverflow;
			details._mmAnimating = false;
			if (onFinish) onFinish();
		};
		anim.oncancel = () => {
			content.style.overflow = prevOverflow;
			details._mmAnimating = false;
		};
	},
};

/*********************************************************************************************
 * 🎯 UI COMPONENTS MANAGER
 */
const UIComponentsManager = {
	/**
	 * Initialize all UI components
	 */
	init() {
		ReadMoreManager.init();
		ExternalLinksManager.init();
		SplideManager.init();
		DetailsAccordionManager.init();
		DocumentHeightManager.update();
	},

	/**
	 * Handle window resize
	 */
	onResize() {
		DocumentHeightManager.update();
	}
};

/*********************************************************************************************
 * 🌍 GLOBAL ACCESS
 */
window.LoaderManager = LoaderManager;
window.ReadMoreManager = ReadMoreManager;
window.ExternalLinksManager = ExternalLinksManager;
window.SplideManager = SplideManager;
window.DetailsAccordionManager = DetailsAccordionManager;
window.DocumentHeightManager = DocumentHeightManager;
window.UIComponentsManager = UIComponentsManager;

/*********************************************************************************************
 * 🎯 AUTO-INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	UIComponentsManager.init();
});

window.addEventListener('resize', window.debounce(() => UIComponentsManager.onResize(), 150));
